"use client";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { IconCheckSm, type IconProps } from "@teral/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@teral/ui";
import { cn } from "cn";
import type { Route } from "next";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type * as React from "react";
import { type ComponentType, type ReactNode, useState } from "react";

export type CrumbPopoverItem = {
  id: string;
  label: string;
  href?: string;
  onClick?: () => void;
  badge?: ReactNode;
};

export type CrumbPopoverFooter = {
  icon: ComponentType<IconProps>;
  label: string;
  href?: string;
  onClick?: () => void;
};

type CrumbPopoverProps = {
  items: CrumbPopoverItem[];
  currentId?: string;
  searchPlaceholder: string;
  emptyText: string;
  footer: CrumbPopoverFooter;
  children: ReactNode;
  listStatus?: ReactNode;
  side?: "bottom" | "right";
};

export function CrumbPopover({
  items,
  currentId,
  searchPlaceholder,
  emptyText,
  footer,
  children,
  listStatus,
  side = "bottom",
}: CrumbPopoverProps) {
  const [open, setOpen] = useState(false);
  const router = useRouter();

  const selectItem = (item: CrumbPopoverItem) => {
    setOpen(false);
    if (item.href) {
      router.push(item.href as Route);
    } else if (item.onClick) {
      item.onClick();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={children as React.ReactElement} />
      <PopoverContent side={side} align="start" className="w-64 p-0" sideOffset={8}>
        <Command
          filter={(value, search) => {
            if (value.startsWith("__footer__")) {
              return 1;
            }
            if (!search) {
              return 1;
            }
            return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder={searchPlaceholder}
            className="text-sm placeholder:text-gray-8"
          />
          <CommandList>
            {listStatus ?? <CommandEmpty className="py-6">{emptyText}</CommandEmpty>}
            <CommandGroup>
              {items.map((item) => {
                const isCurrent = item.id === currentId;
                return (
                  <CommandItem
                    key={item.id}
                    value={item.label}
                    onSelect={() => selectItem(item)}
                    className="flex items-center gap-2 py-1 mt-0"
                  >
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.badge ? <span className="shrink-0">{item.badge}</span> : null}
                    <IconCheckSm
                      className={cn(
                        "shrink-0 text-gray-12",
                        isCurrent ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              <FooterRow
                footer={footer}
                onSelect={() => {
                  setOpen(false);
                  if (footer.href) {
                    router.push(footer.href as Route);
                  } else if (footer.onClick) {
                    footer.onClick();
                  }
                }}
              />
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FooterRow({
  footer,
  onSelect,
}: {
  footer: CrumbPopoverFooter;
  onSelect: () => void;
}) {
  const Icon = footer.icon;
  const body = (
    <>
      <Icon className="size-3.5 shrink-0 text-gray-11" />
      <span className="flex-1 truncate">{footer.label}</span>
    </>
  );

  if (footer.href) {
    return (
      <CommandItem asChild value={`__footer__${footer.label}`} onSelect={onSelect}>
        <Link
          href={footer.href as Route}
          className="flex items-center gap-2 py-1 mt-0 overflow-hidden"
        >
          {body}
        </Link>
      </CommandItem>
    );
  }

  return (
    <CommandItem
      value={`__footer__${footer.label}`}
      onSelect={onSelect}
      className="flex items-center gap-2 py-1 mt-0"
    >
      {body}
    </CommandItem>
  );
}
